import React from 'react';
import {
  PiFileLight,
  PiFilePdfLight,
  PiFileDocLight,
  PiFileXlsLight,
  PiFilePptLight,
  PiFileTextLight,
  PiFileCsvLight,
  PiImageLight,
  PiEnvelopeSimpleLight,
  PiFileZipLight,
  PiFileCodeLight,
} from 'react-icons/pi';

/**
 * File type → glyph + short label.
 *
 * Four pages each had their own `getFileIcon`, and they disagreed: a PDF was
 * red in one, grey in another, and a `.msg` was a generic page everywhere.
 * The label matters as much as the glyph - "DOCX" in a badge is what tells a
 * user at a glance which of three similarly-named files is the signed one.
 *
 * Colour comes from this table only. Never colour a file row per-call site.
 */

export type FileKind =
  | 'pdf'
  | 'word'
  | 'sheet'
  | 'slides'
  | 'csv'
  | 'text'
  | 'image'
  | 'email'
  | 'archive'
  | 'code'
  | 'other';

interface KindSpec {
  Icon: React.ComponentType<{ className?: string }>;
  /** Token classes - flips with dark mode on its own. */
  tone: string;
}

const KIND: Record<FileKind, KindSpec> = {
  pdf: { Icon: PiFilePdfLight, tone: 'text-danger-500' },
  word: { Icon: PiFileDocLight, tone: 'text-info-500' },
  sheet: { Icon: PiFileXlsLight, tone: 'text-success-500' },
  slides: { Icon: PiFilePptLight, tone: 'text-warning-500' },
  csv: { Icon: PiFileCsvLight, tone: 'text-success-500' },
  text: { Icon: PiFileTextLight, tone: 'text-fg-muted' },
  image: { Icon: PiImageLight, tone: 'text-champagne' },
  email: { Icon: PiEnvelopeSimpleLight, tone: 'text-info-500' },
  archive: { Icon: PiFileZipLight, tone: 'text-warning-500' },
  code: { Icon: PiFileCodeLight, tone: 'text-fg-muted' },
  other: { Icon: PiFileLight, tone: 'text-fg-muted' },
};

const BY_EXTENSION: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  dot: 'word',
  dotx: 'word',
  rtf: 'word',
  odt: 'word',
  xls: 'sheet',
  xlsx: 'sheet',
  xlsm: 'sheet',
  ods: 'sheet',
  csv: 'csv',
  tsv: 'csv',
  ppt: 'slides',
  pptx: 'slides',
  odp: 'slides',
  txt: 'text',
  md: 'text',
  log: 'text',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  heic: 'image',
  msg: 'email',
  eml: 'email',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  json: 'code',
  xml: 'code',
  html: 'code',
};

/** Substring probes against a MIME type, checked in order. */
const BY_MIME: Array<[string, FileKind]> = [
  ['pdf', 'pdf'],
  ['spreadsheet', 'sheet'],
  ['excel', 'sheet'],
  ['presentation', 'slides'],
  ['powerpoint', 'slides'],
  ['csv', 'csv'],
  ['word', 'word'],
  ['officedocument.wordprocessing', 'word'],
  ['image/', 'image'],
  ['message/', 'email'],
  ['outlook', 'email'],
  ['zip', 'archive'],
  ['compressed', 'archive'],
  ['json', 'code'],
  ['xml', 'code'],
  ['html', 'code'],
  ['text/', 'text'],
];

/** Extension wins over MIME - browsers lie about MIME, names rarely do. */
export const fileKindOf = (name?: string, mime?: string): FileKind => {
  const filename = (name || '').toLowerCase();
  const dot = filename.lastIndexOf('.');
  if (dot > 0 && dot < filename.length - 1) {
    const ext = filename.slice(dot + 1);
    if (BY_EXTENSION[ext]) return BY_EXTENSION[ext];
  }

  const type = (mime || '').toLowerCase().trim();
  if (type) {
    // `FileType` columns hold bare tokens ("pdf") as often as real MIME types.
    if (!type.includes('/') && BY_EXTENSION[type]) return BY_EXTENSION[type];
    for (const [probe, kind] of BY_MIME) {
      if (type.includes(probe)) return kind;
    }
  }
  return 'other';
};

/** The short badge label - "PDF", "DOCX", or "FILE" when nothing is known. */
export const fileTypeLabel = (name?: string, mime?: string): string => {
  const filename = (name || '').trim();
  const dot = filename.lastIndexOf('.');
  if (dot > 0 && dot < filename.length - 1) {
    const ext = filename.slice(dot + 1);
    // A long tail after the last dot is not an extension, it is a sentence.
    if (ext.length <= 5) return ext.toUpperCase();
  }
  const type = (mime || '').trim();
  if (type && !type.includes('/') && type.length <= 5) return type.toUpperCase();
  const kind = fileKindOf(name, mime);
  return kind === 'other' ? 'FILE' : kind.toUpperCase();
};

export interface FileIconProps {
  name?: string;
  mime?: string;
  className?: string;
}

/** The glyph for a file. Size it with `className` (defaults to h-5 w-5). */
const FileIcon: React.FC<FileIconProps> = ({ name, mime, className }) => {
  const { Icon, tone } = KIND[fileKindOf(name, mime)];
  return <Icon className={className ? `${tone} ${className}` : `${tone} h-5 w-5`} />;
};

export default FileIcon;
